import { randomUUID } from 'uncrypto';

/**
 * Provides a unique session identifier that works across SSR and client-side rendering.
 *
 * Server-side: Generates a unique UUID per request using `randomUUID()` from uncrypto.
 * Each SSR request gets a distinct session ID that is serialized to the client.
 *
 * Client-side: Persists the session ID to `sessionStorage` for same-tab navigation.
 * On first load, stores the server-generated ID. On subsequent page loads within the
 * same tab, retrieves and reuses the stored ID. Falls back gracefully if sessionStorage
 * is unavailable (private browsing, disabled storage, etc.).
 *
 * @returns {Object} Object containing the readonly session ID ref
 * @returns {Readonly<Ref<string>>} sessionId - A readonly ref containing the UUID string
 *
 * @remarks
 * - Uses `import.meta.server` and `import.meta.client` for environment detection
 * - Session ID persists across page navigation only within the same browser tab
 * - SessionStorage errors are caught and logged without breaking functionality
 */
export function useSession() {
  const sessionId = useState<string>('session_id');

  // Server: generate unique ID per request (only if not already set)
  if (import.meta.server && !sessionId.value) {
    sessionId.value = randomUUID();
  }

  // Client: persist to sessionStorage for same-tab navigation
  if (import.meta.client) {
    try {
      const storedId = sessionStorage.getItem('session_id');
      if (storedId) {
        // Use stored ID if available
        sessionId.value = storedId;
      } else if (sessionId.value) {
        // Store the server-generated ID for future page loads
        sessionStorage.setItem('session_id', sessionId.value);
      }
    } catch (error) {
      // SessionStorage may be unavailable (private mode, disabled storage, etc.)
      // Silently continue without storage - the session ID will still work for this request
      console.warn('SessionStorage unavailable, session ID will not persist across page loads:', error);
    }
  }

  return { sessionId: readonly(sessionId) };
}
